import { useMemo, useCallback } from 'react';
import { SlotInfo, SlotType } from '../../../../../types/schedule.types';
import { parseDate } from '../../../../../utils/dateUtils';

export interface ShootingPeriod {
  startDate: Date;
  endDate: Date;
  totalDays: number;
}

/**
 * Hook for computing shooting period and day number from slots
 * Only considers SHOOT type slots (same as 开机日期 calculation)
 */
export const useShootingPeriod = (slots: SlotInfo[] = []) => {
  const shootingPeriod = useMemo((): ShootingPeriod | null => {
    const slotTypeSlots =
      slots?.filter((pt) => pt.slotType === SlotType.SHOOT || !pt.slotType) ?? [];
    if (slotTypeSlots.length === 0) return null;

    const validDateStrings = slotTypeSlots
      .map((slot) => slot.slotDate)
      .filter((day): day is string => !!day)
      .map((day) => day.split('T')[0])
      .filter((dateStr) => {
        const date = parseDate(dateStr);
        return !isNaN(date.getTime());
      });

    if (validDateStrings.length === 0) return null;

    const uniqueDateStrings = Array.from(new Set(validDateStrings));
    const validDates = uniqueDateStrings.map(parseDate);

    const startDate = new Date(Math.min(...validDates.map((d) => d.getTime())));
    const endDate = new Date(Math.max(...validDates.map((d) => d.getTime())));
    const totalDays = uniqueDateStrings.length;

    return { startDate, endDate, totalDays };
  }, [slots]);

  const getShootingDayNumber = useCallback(
    (date: Date): number | null => {
      const slotTypeSlots =
        slots?.filter((pt) => pt.slotType === SlotType.SHOOT || !pt.slotType) ?? [];
      if (!shootingPeriod || slotTypeSlots.length === 0) return null;

      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      const validDateStrings = slotTypeSlots
        .map((slot) => slot.slotDate)
        .filter((day): day is string => !!day)
        .map((day) => day.split('T')[0])
        .filter((ds) => {
          const d = parseDate(ds);
          return !isNaN(d.getTime());
        });

      const uniqueDateStrings = Array.from(new Set(validDateStrings)).sort();
      const index = uniqueDateStrings.indexOf(dateStr);

      if (index !== -1) return index + 1;
      return null;
    },
    [shootingPeriod, slots]
  );

  return { shootingPeriod, getShootingDayNumber };
};
